/**
 * @docs docs/features/website-navigation-shell
 */

/*----------------------------------
- MOBILE SITE MENU
----------------------------------*/

import React from 'react';

import { Button } from '@uniquedomains/components/kit/button/Button';
import Icon from '@/client/components/ui/icon/Icon';
import {
    DOMAINS_MENU_COLUMN_TITLES,
    type LandingHeaderDomainsNavItem,
} from '@/client/catalogs/landing/pageContent';
import { LANDING_MARKET_MENU_GROUPS } from '@/client/catalogs/landing/marketMenu';
import { LANDING_MENU_PANEL_COPY } from '@/client/catalogs/landing/menuPanels';
import { LANDING_TOOLS_MENU_GROUPS } from '@/client/catalogs/landing/toolsMenu';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import { setLandingBodyClass } from '@/client/pages/landing/lib/documentOverlayState';
import type { Links } from '../../types';
import DomainsMenuChip from './DomainsMenuChip';
import MenuGroupColumns from './MenuGroupColumns';
import MenuRow from './MenuRow';
import MobileMenuSection from './MobileMenuSection';
import { formatTldLabel, getDomainsMenuColumnItems } from './menuShared';
import { NAV_SCREENER_ITEM, type MenuNavigateHandler } from './navClick';
import useDrawerGrabClose from './useDrawerGrabClose';

/*----------------------------------
- TYPES
----------------------------------*/

export type MobileSiteMenuSectionId = 'domains' | 'market' | 'tools';

type MobileSiteMenuProps = {
    /** The dialog's id, which the header's menu button names in `aria-controls`. */
    id: string;
    isOpen: boolean;
    links: Links;
    /** Null while the public Domains hub is unpublished, which drops the whole section. */
    domainsMenu: LandingHeaderDomainsNavItem | null;
    /**
     * The site header, which stays over the open drawer with the logo and the menu button (now its close button): the
     * Tab cycle runs through its visible controls and the drawer's.
     */
    headerRef: { readonly current: HTMLElement | null };
    isMember: boolean;
    /**
     * A member's way into Product, the pill's own "Open app" (href, label and handler from Header): the pill's call to
     * action is hidden below 1024px, so without this row a member on a phone had no way into Product from the frame
     * (batch 7 review M5, Gaetan's 2026-10-27 answer A). Null for a visitor, who gets Log in instead.
     */
    openApp: { href: string; label: string; onClick: () => void } | null;
    onClose: () => void;
    /**
     * Follows a menu link: records it, then closes the dialog. Log in and GitHub keep their
     * own handlers because they already emit a conversion trigger and a GitHub event.
     */
    onNavigate: MenuNavigateHandler;
    onLogin: () => void;
    onGithub: () => void;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled])';
/** The body class the drawer's open look keys on (marketing.css: `.msm-open .msm`, `.msm-open .hdr`, the scrim). */
export const MOBILE_SITE_MENU_OPEN_CLASS = 'msm-open';
/** How long the drawer stays in the document after it starts closing: its 0.28s slide up, with room to finish. */
const HIDE_DELAY_MS = 320;
/** How long the opening cascade class stays on (`.is-cascade`): the last item's delay plus its 0.42s drop. */
const CASCADE_MS = 900;
/** When an unfolded section's foot passes the drawer's, the list scrolls it up once the 0.36s unfold has run. */
const UNFOLD_SCROLL_DELAY_MS = 380;
/** The items that drop in turn on open, in document order (marketing.css, `.msm.is-cascade`). */
const CASCADE_SELECTOR = '.msm-sec, .msm-nav > a.msm-row, .msm-plain > *, .msm-copy';
const CONTACT_HREF = `${LANDING_WEBSITE_PAGE_ROUTES.team}#contact`;

export const MOBILE_SITE_MENU_SECTION_IDS: Record<MobileSiteMenuSectionId, string> = {
    domains: 'landing-mobile-domains-menu',
    market: 'landing-mobile-market-menu',
    tools: 'landing-mobile-tools-menu',
};

/*----------------------------------
- HELPERS
----------------------------------*/

const prefersReducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A control a keyboard can reach now: laid out and not folded away (`visibility: hidden` keeps a box). */
const isReachable = (element: HTMLElement): boolean =>
    element.getClientRects().length > 0 && window.getComputedStyle(element).visibility !== 'hidden';

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * The phone navigation, the page template's white drawer (Home v3, Gaetan 2026-11-04, which replaced W's full-screen
 * sheet): it drops from the top over the page dimmed and blurred by its scrim, as tall as its items, while the header's
 * logo and menu button stay over it (the button turns into its close button, Header.tsx). Inside, the three megamenus
 * become in-place disclosures, one open at a time, then Pricing, then Log in (Open app for a member), GitHub lists and
 * Contact as rail items, and the grab bar at its foot closes it on a tap or a drag up (`useDrawerGrabClose`). Log in
 * stays here even though the desktop bar dropped it: the phone has no other in-menu way back into an existing account,
 * and no bottom action bar since 2026-10-27.
 *
 * Open and close: `isOpen` is the header's intent. On open the drawer leaves `hidden`, its items take their cascade
 * order, then the body class lands one reflow later so the slide and the fade run from their closed state; on close the
 * class leaves first and the drawer goes back to `hidden` once its slide is over (at once under reduced motion). The
 * page under it does not scroll (Header.tsx's scroll lock).
 */
export default function MobileSiteMenu({
    id,
    isOpen,
    links,
    domainsMenu,
    headerRef,
    isMember,
    openApp,
    onClose,
    onNavigate,
    onLogin,
    onGithub,
}: MobileSiteMenuProps) {
    const dialogRef = React.useRef<HTMLDivElement | null>(null);
    const scrimRef = React.useRef<HTMLDivElement | null>(null);
    const navRef = React.useRef<HTMLElement | null>(null);
    const [isShown, setIsShown] = React.useState(isOpen);
    const [isNavScrolled, setIsNavScrolled] = React.useState(false);
    const [openSection, setOpenSection] = React.useState<MobileSiteMenuSectionId | null>(null);
    const { onGrabPointerDown, onDrawerClickCapture } = useDrawerGrabClose(dialogRef, scrimRef, onClose);

    // A section that unfolds past the drawer's foot scrolls up to its row once the unfold has run, as the mock does.
    const unfoldTimerRef = React.useRef<number | undefined>(undefined);
    const toggleSection = (sectionId: MobileSiteMenuSectionId) => {
        const opening = openSection !== sectionId;
        setOpenSection(opening ? sectionId : null);
        window.clearTimeout(unfoldTimerRef.current);
        if (!opening) return;

        unfoldTimerRef.current = window.setTimeout(() => {
            const nav = navRef.current;
            const section = document.getElementById(MOBILE_SITE_MENU_SECTION_IDS[sectionId])?.parentElement;
            if (!nav || !section) return;
            const box = nav.getBoundingClientRect();
            const sectionBox = section.getBoundingClientRect();
            if (sectionBox.bottom > box.bottom) {
                nav.scrollTo({ top: nav.scrollTop + sectionBox.top - box.top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
            }
        }, prefersReducedMotion() ? 0 : UNFOLD_SCROLL_DELAY_MS);
    };

    // Closing: the open look leaves at once, the drawer leaves the document after its slide.
    React.useLayoutEffect(() => {
        if (isOpen) {
            setIsShown(true);
            return;
        }

        setLandingBodyClass(MOBILE_SITE_MENU_OPEN_CLASS, false);
        const hideTimer = window.setTimeout(() => setIsShown(false), prefersReducedMotion() ? 0 : HIDE_DELAY_MS);
        return () => window.clearTimeout(hideTimer);
    }, [isOpen]);

    // Opening, once the drawer is in the document: the list back at its top, the cascade order, one reflow so the
    // transitions start from the closed state, then the open look and the first focus inside the drawer.
    React.useLayoutEffect(() => {
        const dialog = dialogRef.current;
        if (!isOpen || !isShown || !dialog) return;

        if (navRef.current) navRef.current.scrollTop = 0;
        setIsNavScrolled(false);
        dialog.querySelectorAll<HTMLElement>(CASCADE_SELECTOR).forEach((element, index) => element.style.setProperty('--i', String(index)));
        // Set on the element, not through state: a re-render would replay the drop of every item.
        dialog.classList.add('is-cascade');
        const cascadeTimer = window.setTimeout(() => dialog.classList.remove('is-cascade'), CASCADE_MS);
        void dialog.offsetWidth;
        setLandingBodyClass(MOBILE_SITE_MENU_OPEN_CLASS, true);
        // The first row takes the focus (it carries the drawer's butter ring); W's sheet gave it to its own close button
        // (web-frame-04), which is now the header's menu button, outside the drawer.
        dialog.querySelector<HTMLElement>('.msm-row')?.focus({ preventScroll: true });

        return () => {
            window.clearTimeout(cascadeTimer);
            dialog.classList.remove('is-cascade');
        };
    }, [isOpen, isShown]);

    React.useEffect(
        () => () => {
            window.clearTimeout(unfoldTimerRef.current);
            setLandingBodyClass(MOBILE_SITE_MENU_OPEN_CLASS, false);
        },
        [],
    );

    // Escape closes; Tab cycles through the header's visible controls (the logo, the close button) and the drawer's.
    // Folded sections are `visibility: hidden`, out of the cycle, so the live set is read per keypress.
    React.useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') return onClose();
            if (event.key !== 'Tab') return;

            const scopes = [headerRef.current, dialogRef.current];
            const focusable = scopes
                .flatMap((scope) => Array.from(scope?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) || []))
                .filter(isReachable);
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (!first || !last) return;

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [headerRef, isOpen, onClose]);

    return (
        <>
            {/* The dimmed, blurred page; a tap on it closes the drawer. */}
            <div ref={scrimRef} class="msm-scrim" hidden={!isShown} aria-hidden="true" onClick={onClose} />
            <div
                ref={dialogRef}
                id={id}
                class="msm"
                hidden={!isShown}
                role="dialog"
                aria-modal="true"
                aria-label="Site navigation"
                data-testid="mobile-site-menu"
                onClickCapture={onDrawerClickCapture}
            >
                {/* `aria-modal` hides the header from a screen reader that honours it (VoiceOver), and the header's button
                    is the visible close control: this one is the dialog's own, read first and kept out of the Tab cycle,
                    which already passes through the header's button (self-review m1, 2026-11-04). */}
                <button type="button" class="ud-sr-only" tabIndex={-1} onClick={onClose}>
                    Close navigation menu
                </button>
                {/* The list scrolls on one axis only, inside its own nav (`.msm-nav`: overflow-y auto, overscroll contained);
                    its top edge shows a hairline once it has scrolled. */}
                <nav
                    ref={navRef}
                    aria-label="Site navigation"
                    class={isNavScrolled ? 'msm-nav is-scrolled' : 'msm-nav'}
                    onScroll={(event) => setIsNavScrolled(event.currentTarget.scrollTop > 0)}
                >
                    {domainsMenu ? (
                        <MobileMenuSection
                            id={MOBILE_SITE_MENU_SECTION_IDS.domains}
                            label="Domains"
                            isOpen={openSection === 'domains'}
                            onToggle={() => toggleSection('domains')}
                            testId="mobile-header-domains-disclosure"
                            bodyTestId="mobile-domains-menu"
                        >
                            <div>
                                <div class="msm-lab">{DOMAINS_MENU_COLUMN_TITLES.lists}</div>
                                <ul class="msm-list">
                                    {getDomainsMenuColumnItems(domainsMenu, DOMAINS_MENU_COLUMN_TITLES.lists).map(
                                        (item) => (
                                            <li key={item.label}>
                                                <MenuRow
                                                    href={item.href || links.screener}
                                                    label={item.label}
                                                    description={item.description}
                                                    iconName={item.iconName}
                                                    density="mobile"
                                                    onClick={() => onNavigate(item.label, item.href || links.screener)}
                                                />
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </div>
                            <div>
                                <div class="msm-lab">
                                    <abbr title="Top-level domains: the ending, like .com or .ai" class="msm-abbr">
                                        {DOMAINS_MENU_COLUMN_TITLES.endings}
                                    </abbr>
                                </div>
                                <div class="msm-chips">
                                    {getDomainsMenuColumnItems(domainsMenu, DOMAINS_MENU_COLUMN_TITLES.endings).map(
                                        (item) => (
                                            <DomainsMenuChip
                                                key={item.label}
                                                item={item}
                                                fallbackHref={links.screener}
                                                isTld
                                                onClick={() =>
                                                    onNavigate(formatTldLabel(item.label), item.href || links.screener)}
                                            />
                                        ),
                                    )}
                                    <a
                                        href={LANDING_WEBSITE_PAGE_ROUTES.tldHub}
                                        onClick={() => onNavigate(LANDING_MENU_PANEL_COPY.domains.allTldsLabel, LANDING_WEBSITE_PAGE_ROUTES.tldHub)}
                                        class="ud-chip-filter"
                                        data-testid="mobile-domains-menu-all-tlds"
                                    >
                                        {LANDING_MENU_PANEL_COPY.domains.allTldsLabel}
                                    </a>
                                </div>
                            </div>
                            <div>
                                <div class="msm-lab">{DOMAINS_MENU_COLUMN_TITLES.sectors}</div>
                                <div class="msm-chips">
                                    {getDomainsMenuColumnItems(domainsMenu, DOMAINS_MENU_COLUMN_TITLES.sectors).map(
                                        (item) => (
                                            <DomainsMenuChip
                                                key={item.label}
                                                item={item}
                                                fallbackHref={links.screener}
                                                onClick={() => onNavigate(item.label, item.href || links.screener)}
                                            />
                                        ),
                                    )}
                                    {/* The one way out of the two chip lists, as the mock's sheet draws it (the desktop
                                        panel has no such button since its head went; "All one-word" opens the same page). */}
                                    <Button
                                        variant="ink"
                                        size="xs"
                                        href={links.screener}
                                        onClick={() => onNavigate(NAV_SCREENER_ITEM, links.screener)}
                                        iconRight={<Icon name="arrow-right" size={12} strokeWidth={2.4} />}
                                    >
                                        Open the screener
                                    </Button>
                                </div>
                            </div>
                        </MobileMenuSection>
                    ) : null}

                    <MobileMenuSection
                        id={MOBILE_SITE_MENU_SECTION_IDS.market}
                        label="Market"
                        isOpen={openSection === 'market'}
                        onToggle={() => toggleSection('market')}
                        testId="mobile-header-market-disclosure"
                        bodyTestId="mobile-market-menu"
                    >
                        {/* The desktop panel's three groups under their titles, in the one catalog order (decision E2 = B). */}
                        <MenuGroupColumns groups={LANDING_MARKET_MENU_GROUPS} density="mobile" onNavigate={onNavigate} testIdPrefix="mobile-market-menu" />
                    </MobileMenuSection>

                    <MobileMenuSection
                        id={MOBILE_SITE_MENU_SECTION_IDS.tools}
                        label="Tools"
                        isOpen={openSection === 'tools'}
                        onToggle={() => toggleSection('tools')}
                        testId="mobile-header-tools-disclosure"
                        bodyTestId="mobile-tools-menu"
                    >
                        {/* The desktop panel's two groups on grey tiles, as the panel (decision E2 = B). */}
                        <MenuGroupColumns groups={LANDING_TOOLS_MENU_GROUPS} density="mobile" onNavigate={onNavigate} testIdPrefix="mobile-tools-menu" />
                    </MobileMenuSection>

                    <a href={links.pricing} onClick={() => onNavigate('pricing', links.pricing)} class="msm-row">
                        <b>Pricing</b>
                        <span class="msm-g ud-icon-btn" aria-hidden="true">
                            <Icon name="arrow-right" size={16} strokeWidth={2.2} />
                        </span>
                    </a>

                    {/* Rail items with a trailing arrow, as drawn: the kit `RailItem` puts its icon before the label, so
                        these keep the system class on their own anchors. */}
                    <div class="msm-plain">
                        {openApp ? (
                            <a href={openApp.href} onClick={openApp.onClick} class="ud-rail-item" data-testid="mobile-open-app">
                                {openApp.label}
                                <Icon name="arrow-right" size={16} strokeWidth={2.2} />
                            </a>
                        ) : null}
                        <a href={links.signIn} onClick={onLogin} hidden={isMember} class="ud-rail-item">
                            Log in
                            <Icon name="arrow-right" size={16} strokeWidth={2.2} />
                        </a>
                        <a href={links.githubLists} target="_blank" rel="noreferrer" onClick={onGithub} class="ud-rail-item">
                            GitHub lists
                            <Icon name="arrow-up-right" size={16} strokeWidth={2.2} />
                        </a>
                        <a href={CONTACT_HREF} onClick={() => onNavigate('contact', CONTACT_HREF)} class="ud-rail-item">
                            Contact
                            <Icon name="arrow-right" size={16} strokeWidth={2.2} />
                        </a>
                    </div>

                    <p class="msm-copy">© {new Date().getFullYear()} Unique Domains · Where domain decisions are made.</p>
                </nav>
                <button
                    type="button"
                    class="msm-grab"
                    aria-label="Close navigation menu"
                    onClick={onClose}
                    onPointerDown={onGrabPointerDown}
                    data-testid="mobile-site-menu-grab"
                />
            </div>
        </>
    );
}
